import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  push,
  query,
  orderByChild,
  limitToLast,
  equalTo,
  serverTimestamp,
  increment,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";

// Firebase Configuration
const firebaseConfig = {
  apiKey: "AIzaSyBWXO-j9eLCyCxYkby7NTlHPeunImyjQKE",
  authDomain: "parlayground-605ca.firebaseapp.com",
  projectId: "parlayground-605ca",
  storageBucket: "parlayground-605ca.firebasestorage.app",
  messagingSenderId: "275886196344",
  appId: "1:275886196344:web:d571fc9688d8e497660f8d",
  measurementId: "G-DN8XEVYCX0",
  databaseURL: "https://parlayground-605ca-default-rtdb.firebaseio.com",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const database = getDatabase(app);
const storage = getStorage(app);
const functions = getFunctions(app, "us-central1");

// Uncomment for local development with Firebase emulator
// connectFunctionsEmulator(functions, 'localhost', 5001);

// Auth Providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: "select_account",
});

const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

// Auth Functions
export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const signInWithApple = () => signInWithPopup(auth, appleProvider);
export const signInWithEmail = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);
export const signUpWithEmail = (email: string, password: string) =>
  createUserWithEmailAndPassword(auth, email, password);
export const resetPassword = (email: string) =>
  sendPasswordResetEmail(auth, email);
export const logOut = () => signOut(auth);

// Database Helper Functions
export const dbRef = (path: string) => ref(database, path);
export const dbGet = (path: string) => get(ref(database, path));
export const dbSet = (path: string, data: any) =>
  set(ref(database, path), data);
export const dbUpdate = (path: string, data: any) =>
  update(ref(database, path), data);
export const dbRemove = (path: string) => remove(ref(database, path));
export const dbPush = (path: string, data: any) =>
  push(ref(database, path), data);
export const dbQuery = query;
export const dbOrderByChild = orderByChild;
export const dbLimitToLast = limitToLast;
export const dbEqualTo = equalTo;

// Subscribe to database changes
export const subscribeToPath = (
  path: string,
  callback: (data: any) => void,
  errorCallback?: (error: Error) => void
) => {
  const unsubscribe = onValue(
    ref(database, path),
    (snapshot) => {
      callback(snapshot.val());
    },
    errorCallback
  );
  return unsubscribe;
};

// Storage Functions
export const uploadFile = async (path: string, file: File): Promise<string> => {
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
};

// Cloud Functions
export const callFunction = <T = any, R = any>(name: string) =>
  httpsCallable<T, R>(functions, name);

// Re-export database paths for centralized access
export { DB_PATHS, getPath, createPath, TEST_PREFIX, PROD_PREFIX } from './databasePaths';

export {
  app,
  auth,
  database,
  storage,
  functions,
  onAuthStateChanged,
  serverTimestamp,
  increment,
  type FirebaseUser,
};
